<template>
   <LoadingPage v-if="isDownloading || isBuildingTable"></LoadingPage>
   <div class="card">
      <div class="card-body m-2">
         <div class="row">
            <div class="collapse-transition"
               :class="[isCollapsed ? 'col-12' : (isPricingTableActive ? 'col-5' : 'col-8')]">
               <div class="card">
                  <div class="spark-doc-header">
                     <div class="spark-doc-header__left">
                        <router-link :to="{ name: 'contracts_v2' }" class="spark-doc-header__back-btn" title="Back to Contracts">
                           <i class="fa-solid fa-arrow-left"></i>
                        </router-link>
                        <h5 class="spark-doc-header__title">
                           <strong>Contract</strong>
                           <span v-if="contractStatus !== null" class="spark-pill-badge"
                              :class="getContractStatusBadgeClass(contractStatus)"
                              :title="sentAtTooltip">
                              <i :class="getContractStatusIcon(contractStatus)"></i>
                              {{ getStatusText(contractStatus) }}
                              <template v-if="sentTimeAgo && (contractStatus === 1 || contractStatus === 2 || contractStatus === '1' || contractStatus === '2')">
                                 • {{ sentTimeAgo }}
                              </template>
                           </span>
                           <span v-if="hasRevisionNotSent" class="spark-pill-badge spark-pill-badge--unsent-edits" title="Unsent edits or resolved revisions are pending.">
                              <i class="fa-solid fa-triangle-exclamation"></i> Unsent Edits
                           </span>
                        </h5>
                     </div>

                     <div class="spark-doc-header__right">
                        <!-- ASK APPROVAL: only when draft (0) or declined (8) -->
                        <button v-if="!this.$store.state.auth.isGeneralUser && !isApproved && (contractStatus === 0 || contractStatus === 8)"
                           type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Request Approval"
                           @click="$event.stopPropagation(); openRequestApprovalModal()">
                           <i class="fa-solid fa-user-check"></i>
                        </button>

                        <!-- APPROVE: only for designated approver when PendingApproval -->
                        <button v-if="!this.$store.state.auth.isGeneralUser && contractStatus === 6 && !isApproved && isDesignatedApprover && this.$store.getters['auth/canSendProposal']"
                           type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Approve Contract"
                           @click="$event.stopPropagation(); approveContract()">
                           <i class="fa-solid fa-check"></i>
                        </button>

                        <!-- DECLINE: only for designated approver when PendingApproval -->
                        <button v-if="!this.$store.state.auth.isGeneralUser && contractStatus === 6 && !isApproved && isDesignatedApprover && this.$store.getters['auth/canSendProposal']"
                           type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Decline Approval"
                           @click="$event.stopPropagation(); declineApproval()">
                           <i class="fa-solid fa-xmark"></i>
                        </button>

                        <!-- RESOLVE VARIABLE FIELDS -->
                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Resolve Variable Fields"
                           :disabled="isBuildingTable || isContractLocked || !canEditContract"
                           @click="$event.stopPropagation(); resolveVariableFields()">
                           <i class="fa-solid fa-code"></i>
                        </button>

                        <!-- VIEW CONTRACT PAGE -->
                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Preview Contract"
                           @click="$event.stopPropagation(); viewContract()">
                           <i class="fa-solid fa-arrow-up-right-from-square"></i>
                        </button>

                        <!-- EXPORT PDF -->
                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Export PDF" @click.stop="downloadContractPdf">
                           <i class="fa-solid fa-file-pdf"></i>
                        </button>

                        <!-- SAVE -->
                        <button :disabled="isContractLocked || !canEditContract" v-if="!this.$store.state.auth.isGeneralUser"
                           type="button" class="action-btn-circle action-btn-circle--primary" title="Save Contract"
                           @click="$event.stopPropagation(); saveContent([])">
                           <i class="fa-solid fa-floppy-disk"></i>
                        </button>

                        <!-- SEND (disabled when executed (4), cancelled (5), or pending approval (6)) -->
                        <button :disabled="contractStatus === 4 || contractStatus === 5 || contractStatus === 6"
                           v-if="!this.$store.state.auth.isGeneralUser && contractStatus !== 4 && this.$store.getters['auth/canSendProposal']"
                           type="button" class="action-btn-circle action-btn-circle--primary"
                           :title="contractStatus === 6 ? 'Contract is pending approval' : (contractStatus === 4 ? 'Contract is executed' : 'Send Contract')"
                           @click="$event.stopPropagation(); openSendContractModal()">
                           <i class="fa-solid fa-paper-plane"></i>
                           <span v-if="hasRevisionNotSent" class="badge-unsent-dot" title="Unsent edits pending"></span>
                        </button>

                        <!-- EXPAND/COLLAPSE -->
                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button"
                           class="action-btn-circle action-btn-circle--primary"
                           :title="isCollapsed ? 'Show Sidebar' : 'Hide Sidebar (Full Width)'"
                           @click="isCollapsed = !isCollapsed">
                           <i :class="[{ 'fa-solid fa-table-columns': isCollapsed, 'fa-solid fa-expand': !isCollapsed }]"></i>
                        </button>
                     </div>
                  </div>
                  <div v-if="hasRevisionNotSent" class="alert alert-warning d-flex align-items-center border-0 shadow-sm mb-3 mt-2" style="background-color: #fff8e1; border-left: 4px solid #ffb300 !important; border-radius: 8px;">
                     <i class="fa-solid fa-triangle-exclamation me-3" style="color: #ffb300; font-size: 20px;"></i>
                     <div>
                        <strong style="color: #5d4037; font-size: 14px;">Unsent Edits & Revisions</strong>
                        <p class="mb-0 text-muted" style="font-size: 13px;">This contract has unsaved edits or resolved revisions that have not been sent to the client. Please click the send button to dispatch the updated contract.</p>
                     </div>
                  </div>
                  <!-- Single WysiwygEditor for all contracts (JSON sections scaffold injected on load) -->
                  <div class="row">
                     <WysiwygEditor :apiKey="tiny_mce_api_key" :css_code="css_code"
                        @has-unsaved-changes="handleUnsavedChanges" @editor-ready="onEditorReady"
                        :disabled="isContractLocked || !canEditContract" ref="editorRef">
                     </WysiwygEditor>
                  </div>
               </div>
            </div>
            <div class="collapsible-content"
               :class="[isCollapsed ? 'd-none' : (isPricingTableActive ? 'col-7' : 'col-4')]"
               style="margin-top: -20px;">
               <div class="card d-flex flex-column" :style="{ visibility: isCollapsed ? 'hidden' : 'visible', minHeight: '1060px' }">
                  <div class="p-2 border-bottom bg-light">
                     <ul class="spark-inspector-nav">
                        <li class="spark-inspector-nav__item">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Details' }"
                              @click.prevent="ChangeOption('Details')" title="Contract Details">
                              <i class="fa-solid fa-circle-info"></i>
                              <span>Details</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="contractStatus !== 4">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Content' }"
                              @click.prevent="ChangeOption('Content')" title="Content Blocks">
                              <i class="fa-solid fa-cubes"></i>
                              <span>Blocks</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="contractStatus !== 4 && contractSections.length > 0">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Sections' }"
                              @click.prevent="ChangeOption('Sections')" title="Sections">
                              <i class="fa-solid fa-layer-group"></i>
                              <span>Sections</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="contractStatus !== 4">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Revisions' }"
                              @click.prevent="loadAndShowRevisions()" title="Client Revisions">
                              <i class="fa-solid fa-code-compare"></i>
                              <span>Revisions</span>
                              <span v-if="unresolvedRevisions && unresolvedRevisions.length > 0" class="spark-tab-badge bg-danger text-white">
                                 {{ unresolvedRevisions.length }}
                              </span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Activity' }"
                              @click.prevent="ChangeOption('Activity')" title="Activity Log">
                              <i class="fa-solid fa-clock-rotate-left"></i>
                              <span>Activity</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="contractStatus === 4">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Accepted' }"
                              @click.prevent="ChangeOption('Accepted')" title="Accepted Details">
                              <i class="fa-solid fa-circle-check"></i>
                              <span>Accepted</span>
                           </button>
                        </li>
                     </ul>
                  </div>
                  <div v-show="isOption === 'Details'" class="flex-grow-1" style="overflow-y: auto; overflow-x: hidden;">
                     <ContractDetailsForm ref="contractDetailsFormRef" @onInitializeDetails="setItems()" @toggleDiscountCalculation="onToggleDiscountCalculation" />
                  </div>
                  <div v-show="isOption === 'Content'" class="flex-grow-1" style="overflow-y: auto; overflow-x: hidden;">
                     <ContractVariableFields ref="contractVariableFieldsRef" :setItems="itemsForSettings"
                        :isLoading="isBuildingTable"
                        @pricingTableInActive="setPricingTableInActive()"
                        @pricingTableActive="setPricingTableActive()" 
                        @pricing-table-changes="onPricingChange"
                        @rebuild-schedule="rebuildScheduleFromBackend"
                        />
                  </div>
                  <div v-show="isOption === 'Activity'" class="flex-grow-1" style="overflow-y: auto;">
                     <ContractActivity ref="contractActivityRef" />
                  </div>
                  <div v-show="isOption === 'Accepted'" class="flex-grow-1" style="overflow-y: auto; overflow-x: hidden;">
                     <ContractAcceptedDetails :details="acceptedDetails" />
                  </div>

                  <!-- ===== REVISION PANEL ===== -->
                  <div v-show="isOption === 'Revisions'" class="revision-panel" style="height: 900px; max-height: 900px; overflow-y: auto; overflow-x: hidden; padding-bottom: 20px;">
                     <div v-if="isLoadingRevisions" class="text-center py-4">
                        <i class="fa-solid fa-spinner fa-spin"></i> Loading revisions...
                     </div>
                     <div v-else-if="sectionRevisions.length === 0" class="text-center py-4 text-muted">
                        <i class="fa-solid fa-inbox" style="color:#888; font-size:24px;"></i>
                        <p class="mt-2">No revisions have been requested yet.</p>
                     </div>
                     <div v-else>
                        <div v-for="rev in sectionRevisions" :key="rev.id" class="revision-card" :class="'revision-' + rev.action">
                           <div class="revision-card-header">
                              <span class="revision-action-badge">
                                 <i v-if="rev.action === 'comment'" class="fa-regular fa-comment"></i>
                                 <i v-else-if="rev.action === 'revision'" class="fa-solid fa-pen-to-square"></i>
                                 <i v-else class="fa-solid fa-trash-can"></i>
                                 {{ rev.action === 'comment' ? 'Comment' : rev.action === 'revision' ? 'Revision Request' : 'Deletion Request' }}
                              </span>
                              <span class="revision-section-tag">{{ rev.section_name }}</span>
                           </div>
                           <div class="revision-submitter">By: {{ rev.submitted_by || 'Client' }} &middot; {{ formatRevisionDate(rev.created_at) }}</div>
                           <div v-if="rev.content" class="revision-body">{{ rev.content }}</div>
                           <div class="revision-card-footer mt-2 d-flex gap-2">
                              <button class="btn btn-sm btn-outline-info" @click="locateRevision(rev)">
                                 <i class="fa-solid fa-magnifying-glass"></i> Locate
                              </button>
                              <button class="btn btn-sm btn-outline-primary" @click="toggleChat(rev.id)" style="position: relative;">
                                 <i class="fa-regular fa-comments"></i> Chat Thread
                                 <span v-if="revisionUnreadCounts[rev.id]" title="Unread messages" style="position: absolute; top: -6px; right: -6px; background: #e53935; color: #fff; border-radius: 50%; width: 18px; height: 18px; font-size: 10px; display: flex; align-items: center; justify-content: center;">{{ revisionUnreadCounts[rev.id] }}</span>
                              </button>
                              <span v-if="rev.is_ignored" class="badge bg-secondary ms-auto d-flex align-items-center" title="This revision was ignored because the BDM resent the contract with unresolved changes.">Ignored</span>
                              <button v-else-if="!rev.is_resolved" class="btn btn-sm btn-success ms-auto" :disabled="resolvingRevisionId === rev.id || !canEditContract" @click="resolveRevision(rev)">
                                 <i class="fa-solid fa-circle-check"></i>
                                 {{ resolvingRevisionId === rev.id ? 'Resolving...' : 'Mark Resolved' }}
                              </button>
                              <span v-else class="badge bg-success ms-auto d-flex align-items-center">Resolved</span>
                           </div>

                           <!-- CHAT PANEL -->
                           <div v-if="activeChatRevisionId === rev.id" style="margin-top: 15px; border-top: 1px solid #ddd; padding-top: 15px;">
                              <div style="max-height: 250px; overflow-y: auto; margin-bottom: 10px; background: #fdfdfd; padding: 10px; border-radius: 6px;">
                                 <p v-if="chatComments.length === 0" style="color: #888; font-size: 12px; text-align: center;">No messages yet. Start the conversation!</p>
                                 
                                 <div v-for="c in chatComments" :key="c.id" style="margin-bottom: 10px; padding: 8px; border-radius: 6px;" :style="{ background: c.is_internal ? '#e3f2fd' : '#f5f5f5', borderLeft: c.is_internal ? '3px solid #1976d2' : '3px solid #9e9e9e' }">
                                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                                       <strong style="font-size: 12px; color: #333;">{{ c.submitted_by }} <span v-if="c.is_internal" style="color: #1976d2; font-size: 10px;">(Vertex Team)</span></strong>
                                       <span style="font-size: 10px; color: #888;">{{ new Date(c.created_at).toLocaleString() }}</span>
                                    </div>
                                    <p style="font-size: 13px; margin: 0; color: #444; white-space: pre-wrap;">{{ c.comment_text }}</p>
                                 </div>
                              </div>
                              <div style="display: flex; flex-direction: column; gap: 8px;">
                                 <textarea v-model="newCommentText" class="form-control" rows="2" placeholder="Type a reply..." style="font-size: 13px;"></textarea>
                                 <button class="btn btn-primary btn-sm" style="align-self: flex-end;" :disabled="isSubmittingComment || !newCommentText.trim()" @click="postComment(rev.id)">
                                    <i class="fa-solid fa-paper-plane"></i> Send Reply
                                 </button>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
                  <!-- ===== END REVISION PANEL ===== -->

                  <!-- ===== SECTIONS PANEL ===== -->
                  <div v-show="isOption === 'Sections'">
                     <ContractSequence
                        v-model="contractSections"
                        @save="onSectionSequenceSave"
                     />
                  </div>
                  <!-- ===== END SECTIONS PANEL ===== -->
               </div>
               <div style="clear: both;"></div>
            </div>
         </div>
      </div>
   </div>

   <!-- ========== START - FORM ========== -->
   <Teleport to="body">
      <div class="modal fade" id="sendForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="sendForm"
         style="top: 2%; z-index: 1065;">
         <div ref="modalSendFormDialogRef" class="modal-dialog modal-dialog-scrollable modal-lg">
            <div class="modal-content shadow-lg border-0" style="border-radius: 16px; background-color: #fff;">
               <div class="modal-header border-0 pb-0 justify-content-end" style="background-color: transparent;">
                  <div>
                     <button ref="closeSendFormModal" type="button" class="btn-close" data-bs-dismiss="modal"
                        aria-label="Close"></button>
                  </div>
               </div>

               <div class="modal-body p-4 pt-0">
                  <SendForm ref="sendContractForm" 
                     :initialSendTo="contract_signer_emails" 
                     :contractID="contract_id"
                     :contractData="contractData"
                     :initialContractTitle="contract_title"
                     :hasEditor="true"
                     @contractSentSuccess="init(); $refs.closeSendFormModal.click(); setItems();"
                     @close="$refs.closeSendFormModal.click()" />
               </div>
            </div>
         </div>
      </div>
   </Teleport>
   <!-- ========== END - FORM ========== -->




   <!-- REQUEST APPROVAL MODAL -->
   <div v-if="showRequestApprovalModal" class="modal-overlay" @click.self="showRequestApprovalModal = false">
      <div class="modal-content" style="max-width: 450px; padding: 30px; border-radius: 10px;">
         <button class="modal-close-btn" @click="showRequestApprovalModal = false" style="position: absolute; top: 10px; right: 15px; background: none; border: none; font-size: 22px; cursor: pointer;">&times;</button>
         <h5 style="margin-bottom: 20px; color: #00A0C1;">Request Contract Approval</h5>
         <div style="margin-bottom: 15px;">
            <label style="font-weight: 600; font-size: 13px; display: block; margin-bottom: 5px;">Select Approver</label>
            <select v-model="selectedApproverUserId" class="form-control" style="border-radius: 6px;">
               <option :value="null" disabled>-- Select a user --</option>
               <option v-for="user in approverUserList" :key="user.id" :value="user.id">
                  {{ user.first_name }} {{ user.last_name }} ({{ user.email }})
               </option>
            </select>
         </div>
         <div style="display: flex; gap: 10px; justify-content: flex-end;">
            <button class="btn btn-outline-secondary" style="border-radius: 8px !important;" @click="showRequestApprovalModal = false">Cancel</button>
            <button class="btn btn-success" style="border-radius: 8px !important;" :disabled="!selectedApproverUserId" @click="requestApproval()">Send Approval Request</button>
         </div>
      </div>
   </div>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
</template>

<script>
import moment from 'moment'
import { truncateLongText, log, isEmpty } from '@/plugins/helper.js'
import WysiwygEditor from '@/components/Utilities/WysiwygEditor.vue'
import ContractSequence from '@/components/Contracts/ContractSequence.vue'
//import BlockFormEdit from '@/components/ContentLibrary/BlockFormEdit.vue'
import ContractVariableFields from '@/components/Contracts/ContractVariableFields.vue'
import ContractDetailsForm from '@/components/Contracts/ContractDetailsForm.vue'
import ContractActivity from '@/components/Contracts/ContractActivity.vue'
import ContractAcceptedDetails from '@/components/Contracts/ContractAcceptedDetails.vue'

import { nextTick } from 'vue'; // Import nextTick
import SendForm from '@/components/Contracts/SendForm.vue';
import { Modal } from 'bootstrap'; // Import Bootstrap's Modal class
import LoadingPage from '@/components/Utilities/vertex-loading.vue'


export default ({
   components: {
      WysiwygEditor,
      ContractSequence,
      //BlockFormEdit,
      ContractVariableFields,
      ContractDetailsForm,
      ContractActivity,
      ContractAcceptedDetails,
      SendForm,
      LoadingPage,

   },

   props: {
      id: {
         type: String,
      },
   },

   data() {
      return {
         isBuildingTable: false,
         acceptedDetails: {},
         hasRevisionNotSentState: false,
         isDirty: false,
         showResendContractModal: false,
         sentAtState: null,
         sectionRevisions: [],
         isLoadingRevisions: false,
         hasLoadedRevisions: false,
         resolvingRevisionId: null,
         createdByUserId: 0,

         // Chat Module
         activeChatRevisionId: null,
         chatComments: [],
         newCommentText: '',
         isSubmittingComment: false,
         revisionUnreadCounts: {},
         contractSections: [],    // parsed from ContractSectionsJSONDraft
         activeSectionIndex: null, // index of currently focused section
         currentBlockType: null,
         blockData: { /* Your block data here */ }, // Data to pass to your blocks
         componentToRender: {

         },
         block: {
            id: "",
            dateCreated: null,
            blockName: '',
            blockType: '',
            blockUse: '',
            defaultVersion: true,
            color: "#4682B4",
            products: [],
            content: "",
         },
         blockTypes: [],
         blockUse: [],
         productList: [],
         isEditing: false,
         disabledWhenClicked: false,
         sendContractForm: null,
         isDownloading: false,
         debounceFn: null,
         //currentBlockType: "none",
         currentBlockName: "none",
         tiny_mce_api_key: "",
         css_code: "",
         isCollapsed: false,
         isOption: "Details",
         itemsForSettings: {},
         variableFieldsMapping: [
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_organisation_name">` + "organisation_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "organisation_name"
            },
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_abn">` + "abn" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "abn"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_contact_number">` + "contact_number" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "phone_number"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_org_address">` + "org_address" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "org_address",
               backend_only: true
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_first_name">` + "first_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "first_name"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_last_name">` + "last_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "last_name"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_relations_manager">` + "relations_manager" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "relations_manager"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_msp">` + "msp" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "msp"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_billing_email">` + "billing_email" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "email"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_organisation_trading_name">` + "organisation_trading_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "organisation_name"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_client_address_for_service_of_notices">` + "client_address_for_service_of_notices" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "org_address"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_client_email_address_for_service_of_notices">` + "client_email_address_for_service_of_notices" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "email"
            },
            // --- Backend-only variables (resolved from product/contract data, not lead) ---
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_contract_term">` + "contract_term" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "contract_term",
               backend_only: true
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_date_proposal_sent">` + "date_proposal_sent" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "date_proposal_sent",
               backend_only: true
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_termination_date">` + "termination_date" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "termination_date",
               backend_only: true
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_date_executed">` + "date_executed" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "date_executed",
               backend_only: true
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_signers_names">` + "signers_names" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "signers_names",
               backend_only: true
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_subscription_termination_notice">` + "subscription_termination_notice" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "subscription_termination_notice",
               backend_only: true
            }
          ],
         hasNoMinor: false,
         isPricingTableActive: false,
         contract_signer_emails: [],
         contractData: null,
         contract_id: "",
         contractStatus: null, // Add this to hold the current status reactively
         colorForStatus: {
            draft: '#F3994D', 
            sent: '#00a0c1',    
            viewed: '#8A2BE2',   
            in_review: '#FFD700', 
            executed: '#00A54F',
            cancelled: '#EB1C24',
            pending_approval: '#9C27B0',
            approved: '#00A54F',
            declined: '#EB1C24',
            expired: '#EB1C24'
         },
         contract_title: "",
         content_conflict: "",
         content_insurance: "",
         content_terms_conditions: "",
         content_whs_responsibilities: "",
         product_service_content: "",
         cover_page_content: "",
         show_discount_calculation: false,
         filteredContractItems: [],
         canSend: true,

         editorReady: false,
         isEditorReady: false,
         isDataReady: false,
         pendingContent: null,
         hasUnsavedPricingChanges: false,
         isApproved: false,
         approvalRequestedTo: 0,
         showRequestApprovalModal: false,
         selectedApproverUserId: null,
         master_terms_content: "",
         standard_content: "",
         term_content: "",
         subscription_content: "",
         schedule_table_content: "",
         execution_page_content: "",
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingAction: null,
      }
   },

   created() {
      this.tiny_mce_api_key = process.env.VUE_APP_TINY_MCE_API_KEY
//       this.css_code = `
       
//           .image-grid {
//              display: grid;
//              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//              /* Responsive columns */
//              grid-gap: 10px;
//              /* Spacing between grid items */
//           }
 
//           .grid-item {
//              overflow: hidden;
//              border: 3px solid black; /* Add a border to each grid item */
//              /* Prevents image overflow */
//           }
 
//           .grid-item img {
//              width: 100%;
//              height: auto;
//              display: block;
//              /* Prevents image from creating extra space */
//           }
 
//           body { 
//              font-family:"Roboto",sans-serif; 
//              font-size:16px 
//           }
 
//                     .testimonial-container {
//             display: flex;
//             flex-direction: column; /* Stack elements vertically */
//             align-items: center; /* Center horizontally */
//             font-family: sans-serif;
//     border: 1px solid #ddd;
//     padding: 20px;
//     width: 200px; /* Reduced width */
//     text-align: center; /* Center text */
//     margin-bottom: 20px; /* Add some space below each testimonial */
//     position: relative;
// }

// .testimonial-avatar {
//     width: 70px; /* Avatar size */
//     height: 70px;
//     border-radius: 50%;
//     overflow: hidden;
//     margin-bottom: 15px; /* Space below avatar */
// }

// .testimonial-avatar img {
//     width: 100%;
//     height: 100%;
//     object-fit: cover;
// }

// .testimonial-text {
//     font-size: 16px;
//     line-height: 1.4;
//     margin-bottom: 10px;
// }

// .testimonial-author {
//     font-weight: bold;
//     font-size: 14px;
//     margin-top: 0;
// }

// .testimonial-title {
//     font-size: 12px;
//     color: #00A0C1;
// }

// /* New CSS for the remove button */
// .remove-testimonial-button {
//     position: absolute;
//     top: 5px; /* Distance from the top */
//     right: 5px; /* Distance from the right */
//     background-color: #ff4d4d; /* Red background for "remove" */
//     color: white;
//     border: none;
//     border-radius: 50%; /* Make it circular */
//     width: 25px; /* Size of the button */
//     height: 25px; /* Size of the button */
//     font-size: 14px;
//     font-weight: bold;
//     cursor: pointer;
//     display: flex; /* Use flexbox to center 'X' */
//     justify-content: center;
//     align-items: center;
//     padding: 0; /* Remove default padding */
//     line-height: 1; /* Prevent extra line height issues for 'X' */
//     z-index: 10; /* Ensure it's above other content */
//     box-shadow: 0 2px 4px rgba(0,0,0,0.2); /* Optional shadow */
//     transition: background-color 0.2s ease; /* Smooth hover effect */
// }

// .remove-testimonial-button:hover {
//     background-color: orange; /* Darker red on hover */
// }

// .testimonials-grid-wrapper {
//     display: grid; 
//     grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
//     gap: 1px; /* Creates space between grid items (both rows and columns) */
//     padding: 5px; /* Padding around the entire grid container */
//     max-width: 1200px; /* Optional: Limit the maximum width of the grid */
//     margin: 0 auto; /* Optional: Center the grid container on the page */
// }

// .orange-label-product-option {
//     display: inline-block;
//     /* Allows padding and margin to work like a block */
//     background-color: #ff8c00;
//     /* A vibrant orange */
//     color: white;
//     /* White text for contrast */
//     padding: 6px 12px;
//     /* Good spacing around the text */
//     border-radius: 5px;
//     /* Slightly rounded corners */
//     font-weight: bold;
//     /* Bold text */
//     font-size: 0.95em;
//     /* Slightly smaller than default text */
//     text-transform: uppercase;
//     /* All caps for a label look */
//     letter-spacing: 0.5px;
//     /* A little space between letters */
// }
//           `
   },

   mounted() {
      this.init()
      this.sendContractForm = new Modal(this.$refs.sendForm);
   },

   computed: {
      sentTimeAgo() {
         if (!this.sentAtState) return '';
         if (!moment(this.sentAtState).isValid()) return '';
         return moment(this.sentAtState).fromNow();
      },
      sentAtTooltip() {
         if (!this.sentAtState) return '';
         return `Last sent: ${this.formatDate(this.sentAtState)} (${moment(this.sentAtState).fromNow()})`;
      },
      unresolvedRevisions() {
         return this.sectionRevisions.filter(r => !r.is_resolved && !r.is_ignored);
      },
      hasRevisionNotSent() {
         return this.hasRevisionNotSentState;
      },
      item() {
         return this.id
      },
      isDesignatedApprover() {
         const currentUserId = this.$store.state.auth?.currentUser?.id || 0;
         return this.approvalRequestedTo > 0 && currentUserId === this.approvalRequestedTo;
      },
      isContractLocked() {
         // Lock editing when pending approval (6), approved (7), or executed/accepted (4)
         return this.contractStatus === 4 || this.contractStatus === 6 || this.contractStatus === 7;
      },
      approverUserList() {
         // Static fallback user while auth is being migrated
         const staticUsers = [
            { id: 1, first_name: 'Richie', last_name: 'Golez', email: 'ricardo@vtxsecurity.com' }
         ];
         const storeUsers = this.$store.state.auth.usersWithVertexRoles || [];
         if (storeUsers.length > 0) {
            return storeUsers;
         }
         return staticUsers;
      },
      canEditContract() {
         // Lock editing if the contract is locked (pending approval, approved, or executed)
         if (this.contractStatus === 4 || this.contractStatus === 6 || this.contractStatus === 7) {
            return false;
         }
         // Global Super Admin can edit any contract
         if (this.$store.getters['auth/isGlobalSuperAdmin']) {
            return true;
         }
         // Admin Support / Super Admin can edit any contract
         if (this.$store.state.auth.isAdmin || this.$store.state.auth.isSuperAdmin) {
            return true;
         }
         // Spark Consultant can edit if they own the lead/contract
         if (this.$store.state.auth.isConsultant) {
            const currentUserId = this.$store.state.auth?.currentUser?.id || 0;
            const creatorId = this.createdByUserId || 0;
            return creatorId > 0 && currentUserId === creatorId;
         }
         return !this.$store.state.auth.isGeneralUser;
      },
   },

   watch: {
      item() {
         this.sectionRevisions = [];
         this.init();
      },
   },

   methods: {

      onPricingChange(hasChanges) {
         this.hasUnsavedPricingChanges = hasChanges;
      },
      
      // onEditorReady() {
      //    this.editorReady = true;

      //    // If we have content waiting from the API, render it now
      //    if (this.pendingContent) {
      //       this.renderContent(); // Or specific load function
      //    }
      // },

      calculateTotal(item) {
         // Correctly calculates (price - discount) * quantity for a single row
         const price = parseFloat(item.price) || 0;
         const discount = parseFloat(item.discount) || 0;
         const quantity = parseInt(item.quantity) || 0;
         return (price - discount) * quantity;
      },

      calculateOptionSubtotalPrice(productList) {
         // Correctly sums the total of each row to get the subtotal
         return productList.reduce((total, item) => {
            return total + this.calculateTotal(item);
         }, 0);
      },

      calculateOptionGST(productList) {
         // Calculates 10% of the correct subtotal
         return this.calculateOptionSubtotalPrice(productList) * 0.1;
      },

      calculateOptionTotalPrice(productList) {
         // Calculates the subtotal plus GST
         return this.calculateOptionSubtotalPrice(productList) * 1.1;
      },

      formatCurrency(value) {
         const numericValue = parseFloat(value);
         if (isNaN(numericValue)) {
            return '$0.00';
         }
         return `$${numericValue.toFixed(2)}`;
      },

      calculateTotalDiscount(item) {
         return (item.discount * item.quantity);
      },

      toOptionName(count) {
         // Handle the special case: if count is 1, return an empty string
         if (count <= 1) {
            return '';
         }

         let result = '';
         let num = count; // Start with the actual count for conversion

         while (num > 0) {
            // Calculate the remainder when dividing by 26
            // This gives us the position of the current letter (1 for A, 26 for Z)
            let remainder = num % 26;

            // Adjustment for 'Z' and subsequent columns:
            // If remainder is 0, it means the letter is 'Z' (the 26th letter)
            // and we need to decrement num by 26 to properly carry over for the next iteration.
            if (remainder === 0) {
               remainder = 26; // Map 0 remainder to the 26th letter ('Z')
            }

            // Convert the remainder to a character
            // 'A' has ASCII value 65. So, (remainder - 1) + 65 gives us the correct character code
            // (e.g., if remainder is 1, 1-1=0, 65+0=65='A')
            result = String.fromCharCode(64 + remainder) + result;

            // Update num for the next iteration
            // If remainder was 26 (meaning 'Z'), we effectively processed a full '26' group.
            // So, subtract 'remainder' and then divide by 26.
            num = Math.floor((num - remainder) / 26);
         }

         return result;
      },

      setPricingTableInActive() {
         this.isPricingTableActive = false
      },


      setPricingTableActive() {
         this.isPricingTableActive = true
      },

      async openSendContractModal() {
         const hasEditorChanges = this.$refs.editorRef && this.$refs.editorRef.isEditorContentDirty();
         const hasDiscountChanges = this.$refs.contractDetailsFormRef && !this.$refs.contractDetailsFormRef.isShowDiscountChanged();

         if (hasEditorChanges || hasDiscountChanges) {
            const saved = await this.saveContent([], true);
            if (!saved) return;
         }

         if (!this.verifyCanSend())
            return;

         const formRef = this.$refs.contractDetailsFormRef;
         if (formRef && formRef.contract) {
            this.contractData = formRef.contract;
            this.contract_title = formRef.contract.contract_title || this.contract_title;
         }
         this.contract_signer_emails = this.getSignerEmails();

         if (this.$refs.sendContractForm && typeof this.$refs.sendContractForm.updateInitialSendTo === 'function') {
            this.$refs.sendContractForm.updateInitialSendTo(this.contract_signer_emails);
         }

         this.sendContractForm.show();
      },

      isContractSent(versionNumber) {
         // 1. Ensure the version string is a string and starts with 'v'.
         if (typeof versionNumber !== 'string' || !versionNumber.startsWith('v')) {
            return false; // Invalid format or type
         }

         // 2. Remove the 'v' prefix and split the remaining string by '.'.
         //    We expect exactly two parts: major and minor.
         const parts = versionNumber.slice(1).split('.');
         if (parts.length !== 2) {
            return false; // Invalid format, not "major.minor"
         }

         const minorStr = parts[1]; // The minor version is the second part

         // 3. Attempt to convert the minor version string to an integer.
         //    parseInt() will stop at the first non-numeric character.
         //    isNaN() checks if the result is "Not a Number".
         const minor = parseInt(minorStr, 10); // Use radix 10 for decimal conversion

         // Check if parseInt resulted in NaN (meaning minorStr was not a valid number)
         // or if the original minorStr was empty, or if it contained non-numeric characters
         // after a number (e.g., "1a") which parseInt would partially parse to 1.
         // To be strict like Go's Atoi, we can check if the parsed part matches the original string
         // after conversion.
         if (isNaN(minor) || minor.toString() !== minorStr) {
            // This stricter check ensures 'v1.1a' also returns false,
            // as parseInt('1a') would yield 1, but '1'.toString() !== '1a'
            return false; // Minor version is not a valid integer representation
         }

         // 4. Check if the minor version is greater than 0.
         return minor <= 0;
      },

      addContent(content) {
         this.$refs.editorRef.addContent(content)
      },

      setSectionContentById(section_id, content) {

         if (isEmpty(content)) {
            alert("Block is empty")
            return
         }

         const normalizedContent = typeof content === 'string'
            ? content.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:')
            : content;

         this.$refs.editorRef.setSectionContentById(section_id, normalizedContent)
      },

      appendToSectionContentById(section_id, content) {

         if (isEmpty(content)) {
            alert("Block is empty")
            return
         }

         const normalizedContent = typeof content === 'string'
            ? content.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:')
            : content;

         this.$refs.editorRef.appendToSectionContentById(section_id, normalizedContent)
      },

      checkIfIDExist(id_string) {
         return this.$refs.editorRef.checkIfIDExist(id_string)
      },

      setContent(content, isDisabled) {
         this.$refs.editorRef.setContent(content, isDisabled)
      },

      setHeaderTitle(title) {
         this.$parent.title = title
      },

      getContent(content) {
         return this.$refs.editorRef.getContent(content)
      },

      clearIsDirty() {
         this.$refs.editorRef.markEditorContentClean()
      },

      setToDisabled() {
         this.$refs.editorRef.setToDisabled()
      },

      // 1. Create a coordinator function
      tryInitializeContent() {
         // Only proceed if BOTH are ready
         if (this.isEditorReady && this.isDataReady) {
            // Trigger the actual content render here
            this.renderContent();
         }
      },

      // 2. This method listens to the new @editor-ready event from WysiwygEditor
      onEditorReady() {
         this.isEditorReady = true;
         this.tryInitializeContent();
      },

      setItems() {
         nextTick(() => {
            const formRef = this.$refs.contractDetailsFormRef;
            if (!formRef || !formRef.contract) return;
            const contract = formRef.contract;

            this.contractData = contract;
            this.itemsForSettings = this.getContractItems();
            log("items for settings", this.itemsForSettings);
            this.hasNoMinor = this.isContractSent(formRef.getVersion());
            this.contract_signer_emails = this.getSignerEmails();
            this.contract_id = this.id || contract.uuid || contract.id;
            this.contractStatus = contract.contract_status;
            this.contract_title = contract.contract_title;
            this.show_discount_calculation = contract.show_discount_calculation;
            this.hasRevisionNotSentState = contract.has_revision_not_sent || false;
            this.sentAtState = contract.sent_at_raw || contract.sent_at;
            this.createdByUserId = formRef.lead ? formRef.lead.created_by : 0;

            // Load revisions now that contract_id is available
            this.loadAndShowRevisions(true);

            const secContent = contract.contract_section_content || {};
            this.cover_page_content = secContent.cover_page_content_draft || "";
            this.product_service_content = secContent.product_service_content_draft || "";
            this.content_conflict = secContent.content_conflict_draft || "";
            this.content_insurance = secContent.content_insurance_draft || "";
            this.content_whs_responsibilities = secContent.content_whs_responsibilities_draft || "";
            this.content_terms_conditions = secContent.content_terms_conditions_draft || "";
            this.master_terms_content = secContent.master_terms_content_draft || "";
            this.standard_content = secContent.standard_content_draft || "";
            this.term_content = secContent.term_content_draft || "";
            this.subscription_content = secContent.subscription_content_draft || "";
            this.schedule_table_content = secContent.schedule_table_content_draft || "";
            this.execution_page_content = secContent.execution_page_content_draft || "";

            // Load JSON-based sections (new architecture)
            let jsonDraft = secContent.contract_sections_json_draft;
            if (jsonDraft) {
               try {
                  if (typeof jsonDraft === 'string') {
                     jsonDraft = jsonDraft.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:');
                  }
                  this.contractSections = JSON.parse(jsonDraft);
               }
               catch (e) { this.contractSections = []; }
            } else {
               this.contractSections = [];
            }

            this.filterContractItems(formRef.getContractItems());

            // Set approval status
            this.isApproved = contract.is_approved || false;
            this.approvalRequestedTo = contract.approval_requested_to || 0;

            const lead = formRef.lead || {};
            const acceptances = contract.contract_acceptance ? [contract.contract_acceptance] : (contract.contract_acceptances || []);
            const latestAcceptance = acceptances.length > 0 ? acceptances[0] : null;

            const primaryFirst = (latestAcceptance && (latestAcceptance.primary_contact_first_name || latestAcceptance.primary_contact_firstName)) || lead.first_name || '';
            const primaryLast = (latestAcceptance && (latestAcceptance.primary_contact_last_name || latestAcceptance.primary_contact_lastName)) || lead.last_name || '';
            const primaryName = [primaryFirst, primaryLast].filter(Boolean).join(' ') || (latestAcceptance && latestAcceptance.primary_contact_name) || lead.contact_name || '';

            this.acceptedDetails = {
               is_accepted: contract.contract_status === 4 || !!contract.accepted_at,
               accepted_at: contract.accepted_at,
               accepted_by_email: contract.accepted_by_email,
               accepted_by_ip: contract.accepted_by_ip,
               option_index_selected: contract.option_index_selected,
               total_accepted: contract.total_accepted || contract.total_amount,
               accepted_items: (contract.contract_items || []).filter(item => item.added_to_contract),
               organisation_name: (latestAcceptance && latestAcceptance.organisation_name) ? latestAcceptance.organisation_name : (contract.organisation_name || (lead ? lead.organisation_name : '')),
               original_organisation_name: (lead && lead.original_organisation_name) ? lead.original_organisation_name : (contract.original_organisation_name || ''),
               business_number: (latestAcceptance && latestAcceptance.abn) ? latestAcceptance.abn : (contract.abn || (lead ? (lead.abn || lead.abn_info || '') : '')),
               abn: (latestAcceptance && latestAcceptance.abn) ? latestAcceptance.abn : (contract.abn || (lead ? (lead.abn || lead.abn_info || '') : '')),
               original_abn: (lead && lead.original_abn) ? lead.original_abn : (contract.original_abn || ''),
               country: lead?.country_code || lead?.country || contract?.country_code || contract?.country || 'AU',
               organisation_uuid: lead?.organisation_uuid || lead?.auth_organisation_uuid || lead?.organisation?.uuid || contract?.organisation_uuid || '',
               primary_contact_first_name: primaryFirst,
               primary_contact_last_name: primaryLast,
               primary_contact_name: primaryName,
               signers: (latestAcceptance && latestAcceptance.signers) || contract.signers || [],
            };

            if (this.contractStatus === 4 && this.isOption === 'Details') {
               this.isOption = 'Accepted';
            }

            this.isDataReady = true;
            this.tryInitializeContent();
         })
      },

      async downloadContractPdf() {
         this.$toast.info("Generating PDF, please wait...");
         this.isDownloading = true;

         try {
            const uniqueKey = this.$refs.contractDetailsFormRef.getUniqueKey();
            const accessKey = this.$refs.contractDetailsFormRef.getInternalAccessKey();

            const response = await this.$axios.get(
               `/contracts/${uniqueKey}/${accessKey}/pdf`,
               { responseType: 'blob' }
            );

            // Create a download link from the blob
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const sanitizedTitle = (this.contract_title || 'Contract').replace(/[^a-z0-9\s]/gi, '_').replace(/\s+/g, '_');
            link.download = `${sanitizedTitle}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.$toast.success("PDF downloaded successfully!");
         } catch (error) {
            console.error('PDF download error:', error);
            this.$toast.error("Failed to download PDF. Please try again.");
         } finally {
            this.isDownloading = false;
         }
      },

      filterContractItems(contract_items) {
         if (!isEmpty(contract_items)) {
            this.filteredContractItems = [];
            const groupedItems = {};
            contract_items.forEach((p) => {
               if (!groupedItems[p.option_index]) {
                  groupedItems[p.option_index] = { productList: [], option_index: p.option_index };
               }
               groupedItems[p.option_index].productList.push(p);
            });
            this.filteredContractItems = Object.values(groupedItems);
            this.filteredContractItems.sort((a, b) => a.option_index - b.option_index);
         } else {
            this.filteredContractItems = [];
         }
      },

      getContractItems() {
         return {
            items: this.$refs.contractDetailsFormRef.getContractItems(),
            is_charity: true
         }
      },

      getSignerEmails() {
         const contract = this.$refs.contractDetailsFormRef ? this.$refs.contractDetailsFormRef.contract : (this.contractData || {});
         let signers = [];
         const seenEmails = new Set();

         const addSigner = (s) => {
            if (!s || !s.email) return;
            const clean = s.email.trim().toLowerCase();
            if (!clean || seenEmails.has(clean)) return;
            seenEmails.add(clean);

            const first = s.first_name || s.firstName || '';
            const last = s.last_name || s.lastName || '';
            const full = [first, last].filter(Boolean).join(' ') || s.displayName || s.name || s.signer_name || s.email;

            signers.push({
               displayName: full,
               first_name: first,
               last_name: last,
               name: full,
               email: s.email.trim(),
               has_signed: !!(s.has_signed || s.signed_at),
               signed_at: s.signed_at || null,
               isSigner: true
            });
         };

         // 1. Check acceptance signers
         const acceptances = contract.contract_acceptance ? [contract.contract_acceptance] : (contract.contract_acceptances || []);
         const latestAcceptance = acceptances.length > 0 ? acceptances[0] : null;
         if (latestAcceptance && latestAcceptance.signers && latestAcceptance.signers.length > 0) {
            latestAcceptance.signers.forEach(s => addSigner(s));
         }

         // 2. Pull signer emails from shared_users
         if (contract.shared_users && contract.shared_users.length > 0) {
            contract.shared_users.forEach(su => {
               if (su.share_type === 'signer' && su.email) {
                  const hasSigned = !!(contract.is_accepted || contract.contract_status === 4 || contract.contract_status === 7);
                  addSigner({
                     ...su,
                     has_signed: hasSigned,
                     signed_at: hasSigned ? contract.executed_at : null
                  });
               }
            });
         }

         // 3. Fallback to Lead if no signers found
         const lead = contract.Lead || (this.$refs.contractDetailsFormRef ? this.$refs.contractDetailsFormRef.lead : null);
         if (signers.length === 0 && lead && lead.email) {
            const hasSigned = !!(contract.is_accepted || contract.contract_status === 4 || contract.contract_status === 7);
            addSigner({
               first_name: lead.first_name,
               last_name: lead.last_name,
               name: [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.name,
               email: lead.email,
               has_signed: hasSigned,
               signed_at: hasSigned ? contract.executed_at : null
            });
         }

         // 4. Fallback to client_lead_email if no signers found
         if (signers.length === 0 && contract.client_lead_email) {
            addSigner({
               email: contract.client_lead_email,
               displayName: contract.client_lead_email
            });
         }

         return signers;
      },

      async onToggleDiscountCalculation(val) {
         this.show_discount_calculation = val;
         await this.saveContent([], true);
      },

      async saveContent(contract_items = [], skipResendPrompt = false) {
        this.isBuildingTable = true;
        try {
            if (!contract_items.length && !skipResendPrompt) {
                const editor = this.$refs.contractEditor && this.$refs.contractEditor.getEditor();
                if (editor && !editor.isDirty()) {
                    return true;
                }
                if (this.$refs.editorRef && !this.$refs.editorRef.isEditorContentDirty() && this.$refs.contractDetailsFormRef && this.$refs.contractDetailsFormRef.isShowDiscountChanged() == true) {
                    this.$toast.info("No changes have been made.");
                    return true;
                }
            }

            let contract_content = this.getContent()

            let contract_section_content = {
                // cover_page_content: this.getSectionContentById("start_cover_section"),
                // product_service_content: this.getSectionContentById("start_product_services_section"),
                // pricing_table_content: this.getSectionContentById("start_pricing_table_section"),
                // image_grid_content: this.getSectionContentById("start_image_grid_section"),
                // testimonials_content: this.getSectionContentById("start_testimonials_section"),
                // // content_content: this.getSectionContentById("start_content_blocks_section"),
                // content_conflict: this.getSectionContentById("start_content_conflict_section"),
                // content_insurance: this.getSectionContentById("start_content_insurance_section"),
                // content_whs_responsibilities: this.getSectionContentById("start_content_whsr_section"),
                // content_terms_conditions: this.getSectionContentById("start_content_terms_condition_section"),
                // contract_content: contract_content,

                cover_page_content_draft: this.getSectionContentById("start_cover_section"),
                product_service_content_draft: this.getSectionContentById("start_product_services_section"),
                pricing_table_content_draft: this.getSectionContentById("start_pricing_table_section"),
                image_grid_content_draft: this.getSectionContentById("start_image_grid_section"),
                //testimonials_content_draft: this.getSectionContentById("start_testimonials_section"),
                // content_content: this.getSectionContentById("start_content_blocks_section"),
                content_conflict_draft: this.getSectionContentById("start_content_conflict_section"),
                content_insurance_draft: this.getSectionContentById("start_content_insurance_section"),
                content_whs_responsibilities_draft: this.getSectionContentById("start_content_whsr_section"),
                content_terms_conditions_draft: this.getSectionContentById("start_content_terms_condition_section"),
                master_terms_content_draft: this.getSectionContentById("start_master_terms_section") || "",
                standard_content_draft: this.getSectionContentById("start_standard_section") || "",
                term_content_draft: this.getSectionContentById("start_term_section") || "",
                subscription_content_draft: this.getSectionContentById("start_subscription_section") || "",
                schedule_table_content_draft: this.getSectionContentById("start_schedule_table_section") || "",
                execution_page_content_draft: this.getSectionContentById("start_execution_page_section") || "",
                contract_content: contract_content,
               contract_sections_json_draft: this.contractSections.length > 0
                  ? JSON.stringify(this.readSectionsFromEditor())
                  : '',
            }

            if (!isEmpty(contract_items)) {
                contract_section_content["contract_items"] = contract_items
            }

            // log(contract_section_content)
            // log(contract_content)
            // log("product_list on save:", contract_section_content["contract_items"])

            this.canSend = true

            const success = await this.$refs.contractDetailsFormRef.updateContract(contract_section_content)
            if (success === false) {
               return false;
            }
            if (this.$refs.editorRef && this.$refs.editorRef.markEditorContentClean) {
               this.$refs.editorRef.markEditorContentClean();
            }
            if (this.clearIsDirty) {
               this.clearIsDirty();
            }
            this.canSend = true;
            this.$refs.contractActivityRef.triggerDebounce()
            if (!skipResendPrompt && (this.contractStatus === 1 || this.contractStatus === 2 || this.contractStatus === 3 || this.contractStatus === 8)) {
               this.pendingAction = 'resendContract';
               this.confirmModalTitle = 'Resend Contract';
               this.confirmModalMessage = 'Changes saved successfully. Since this contract was already sent, you must resend it for the changes to reflect in the PDF and Xodo Sign. Would you like to resend the contract now?';
               this.showConfirmModal = true;
               this.showResendContractModal = true;
            }
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            this.isBuildingTable = false;
        }
      },

      async rebuildScheduleFromBackend(contract_items) {
        try {
            this.isBuildingTable = true;
            if (this.contractSections && this.contractSections.length > 0) {
               this.contractSections = this.readSectionsFromEditor()
            }
            const draftJson = this.contractSections && this.contractSections.length > 0
               ? JSON.stringify(this.contractSections)
               : ''
            const contractId = this.$refs.contractDetailsFormRef.contract.id;
            const http = this.$http || this.$axios;
            const response = await http.post(
               `contracts/${contractId}/rebuild-schedule`,
               {
                  contract_items: contract_items || [],
                  contract_sections_json_draft: draftJson,
                  contract_content: this.getContent() || ''
               }
            );
            if (response.data && response.data.contract_sections_json_draft) {
                // Update the editor with rebuilt schedule content
                try {
                    let draftJsonStr = response.data.contract_sections_json_draft;
                    if (typeof draftJsonStr === 'string') {
                        draftJsonStr = draftJsonStr.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:');
                    }
                    this.contractSections = JSON.parse(draftJsonStr);
                    this.renderContent();
                    this.hasRevisionNotSentState = true;
                    this.isDirty = false;
                    const editor = this.$refs.contractEditor && this.$refs.contractEditor.getEditor();
                    if (editor) editor.setDirty(false);
                    if (this.clearIsDirty) this.clearIsDirty();
                } catch (e) {
                    console.error("Failed to parse rebuilt schedule JSON", e);
                }
            }
            this.$toast.success("Schedule rebuilt successfully.");
            // Refresh all data so everything is in sync
            this.init();
            if ([1, 2, 3, 8].includes(this.contractStatus)) {
               this.pendingAction = 'resendContract';
               this.confirmModalTitle = 'Resend Contract';
               this.confirmModalMessage = 'Changes saved successfully. Since this contract was already sent, you must resend it for the changes to reflect in the PDF and Xodo Sign. Would you like to resend the contract now?';
               this.showConfirmModal = true;
               this.showResendContractModal = true;
            }
        } catch (error) {
            console.error("Failed to rebuild schedule:", error);
            this.$toast.error(error.response?.data?.message || "Failed to rebuild schedule.");
        } finally {
            this.isBuildingTable = false;
        }
      },

      getDefaultSectionContent(sectionType = 'ScheduleTable') {
         const blocks = this.allBlocks || (this.$refs.contractVariableFieldsRef && this.$refs.contractVariableFieldsRef.allBlocks) || [];
         const scheduleBlock = blocks.find(b =>
            b && b.BlockType && (b.BlockType.name === sectionType || b.BlockType.name === 'ScheduleTable') && b.default_version
         ) || blocks.find(b =>
            b && b.BlockType && (b.BlockType.name === sectionType || b.BlockType.name === 'ScheduleTable')
         );
         if (scheduleBlock && scheduleBlock.content) {
            return scheduleBlock.content.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:');
         }
         return '';
      },

      // Resolve backend-only variable fields (contract_term, proposal_link, termination_date, etc.)
      // without touching the pricing table. Uses the same backend endpoint but sends no items.
      async resolveVariableFields() {
        try {
            this.isBuildingTable = true;
            if (this.contractSections && this.contractSections.length > 0) {
               this.contractSections = this.readSectionsFromEditor()
            }
            const draftJson = this.contractSections && this.contractSections.length > 0
               ? JSON.stringify(this.contractSections)
               : ''
            const contractId = this.$refs.contractDetailsFormRef.contract.id;
            const http = this.$http || this.$axios;
            const response = await http.post(
               `contracts/${contractId}/rebuild-schedule`,
               {
                  contract_items: this.$refs.contractDetailsFormRef ? this.$refs.contractDetailsFormRef.getContractItems() : (this.contract?.ContractItems || []),
                  contract_sections_json_draft: draftJson,
                  contract_content: this.getContent() || ''
               }
            );
            if (response.data && response.data.contract_sections_json_draft) {
                try {
                    let draftJsonStr = response.data.contract_sections_json_draft;
                    if (typeof draftJsonStr === 'string') {
                        draftJsonStr = draftJsonStr.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:');
                    }
                    this.contractSections = JSON.parse(draftJsonStr);
                    this.renderContent();
                    this.hasRevisionNotSentState = true;
                    this.isDirty = false;
                    const editor = this.$refs.contractEditor && this.$refs.contractEditor.getEditor();
                    if (editor) editor.setDirty(false);
                    if (this.clearIsDirty) this.clearIsDirty();
                } catch (e) {
                    console.error("Failed to parse resolved variable fields JSON", e);
                }
            }
            this.$toast.success("Variable fields resolved successfully.");
            this.init();
            if ([1, 2, 3, 8].includes(this.contractStatus)) {
               this.pendingAction = 'resendContract';
               this.confirmModalTitle = 'Resend Contract';
               this.confirmModalMessage = 'Changes saved successfully. Since this contract was already sent, you must resend it for the changes to reflect in the PDF and Xodo Sign. Would you like to resend the contract now?';
               this.showConfirmModal = true;
               this.showResendContractModal = true;
            }
        } catch (error) {
            console.error("Failed to resolve variable fields:", error);
            this.$toast.error(error.response?.data?.message || "Failed to resolve variable fields.");
        } finally {
            this.isBuildingTable = false;
        }
      },

      sendContract() {

         if (this.$refs.editorRef.isEditorContentDirty() || this.$refs.contractDetailsFormRef.isShowDiscountChanged() == false) {
            this.$toast.error("There are unsaved changes.");
            return
         }

         this.$refs.contractDetailsFormRef.sendContract()
         this.$refs.contractActivityRef.triggerDebounce()
      },

      verifyCanSend() {
         if (this.$refs.editorRef.isEditorContentDirty() || this.$refs.contractDetailsFormRef.isShowDiscountChanged() == false) {
            this.$toast.error("There are unsaved changes.");
            this.canSend = false
            return false
         }

         if(this.hasUnsavedPricingChanges) {
            this.$toast.error("There are unsaved pricing table changes. Please build first.");
            this.canSend = false
            return false
         }

         this.canSend = true
         return true
      },

      async approveContract() {
         this.pendingAction = 'approveContract';
         this.confirmModalTitle = 'Approve Contract';
         this.confirmModalMessage = 'Are you sure you want to approve this contract?';
         this.showConfirmModal = true;
      },

      openRequestApprovalModal() {
         this.selectedApproverUserId = null;
         this.showRequestApprovalModal = true;
      },

      async requestApproval() {
         if (!this.selectedApproverUserId) {
            this.$toast.error("Please select an approver.");
            return;
         }

         const users = this.approverUserList;
         const selectedUser = users.find(u => u.id === this.selectedApproverUserId);
         if (!selectedUser) {
            this.$toast.error("Selected user not found.");
            return;
         }

         try {
            const contractId = this.$refs.contractDetailsFormRef.contract.id;
            const response = await this.$axios.post("/contracts/" + contractId + "/request-approval", {
               approver_id: selectedUser.id,
               approver_email: selectedUser.email,
               approver_name: (selectedUser.first_name || '') + ' ' + (selectedUser.last_name || ''),
            });

            if (response.data.success) {
               this.$toast.success("Approval request sent to " + selectedUser.email);
               this.contractStatus = 6; // PENDING_APPROVAL
               this.approvalRequestedTo = selectedUser.id;
               this.showRequestApprovalModal = false;
               this.init();
            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            console.error(error);
            this.$toast.error(error.response?.data?.message || "Failed to request approval.");
         }
      },

      async declineApproval() {
         this.pendingAction = 'declineApproval';
         this.confirmModalTitle = 'Decline Contract';
         this.confirmModalMessage = 'Are you sure you want to decline this contract? The contract will be reset to draft and can be edited again.';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const action = this.pendingAction;
         this.pendingAction = null;

         if (action === 'approveContract') {
            try {
               const contractId = this.$refs.contractDetailsFormRef.contract.id;
               const response = await this.$axios.post("/contracts/" + contractId + "/approve");
               if (response.data.success) {
                  this.$toast.success(response.data.message);
                  this.isApproved = true;
                  this.contractStatus = 7;
                  this.init();
               } else {
                  this.$toast.error(response.data.message);
               }
            } catch (error) {
               console.error(error);
               this.$toast.error(error.response?.data?.message || "Failed to approve contract.");
            }
         } else if (action === 'declineApproval') {
            try {
               const contractId = this.$refs.contractDetailsFormRef.contract.id;
               const response = await this.$axios.post("/contracts/" + contractId + "/decline-approval");
               if (response.data.success) {
                  this.$toast.success(response.data.message);
                  this.contractStatus = 0;
                  this.isApproved = false;
                  this.approvalRequestedTo = 0;
                  this.init();
               } else {
                  this.$toast.error(response.data.message);
               }
            } catch (error) {
               console.error(error);
               this.$toast.error(error.response?.data?.message || "Failed to decline approval.");
            }
         } else if (action === 'resendContract') {
            const hasEditorChanges = this.$refs.editorRef && this.$refs.editorRef.isEditorContentDirty();
            const hasDiscountChanges = this.$refs.contractDetailsFormRef && !this.$refs.contractDetailsFormRef.isShowDiscountChanged();

            if (hasEditorChanges || hasDiscountChanges) {
               const saved = await this.saveContent([], true);
               if (!saved) return;
            }
            this.openSendContractModal();
         }
      },

      handleUnsavedChanges(status) {
         this.canSend = !status;
      },

      switchBlockType(blockType) {
         this.currentBlockType = blockType;
      },

      toggleBold() {
         this.isBold = !this.isBold;
      },
      toggleItalic() {
         this.isItalic = !this.isItalic;
      },
      toggleUnderline() {
         this.isUnderline = !this.isUnderline;
      },

      init() {
         this.$refs.contractDetailsFormRef.editContract(this.id)
         if (this.$refs.contractActivityRef) {
            this.$refs.contractActivityRef.init(this.id)
         }
      },

      renderContent() {
         if (this.contractSections && this.contractSections.length > 0) {
            // JSON-section mode: inject scaffold directly into TinyMCE
            // Do NOT call triggerDebounce() — that would overwrite with the old contract.content HTML
            this.$nextTick(() => {
               if (this.$refs.editorRef) {
                  const scaffold = this.buildSectionsScaffold(this.contractSections);
                  this.$refs.editorRef.setContent(scaffold, this.isContractLocked);
                  this.$refs.editorRef.markEditorContentClean();
               }
            });
         } else {
            // Legacy mode: load old named-section HTML via the details form
            this.$refs.contractDetailsFormRef.triggerDebounce();
         }
      },

      formatDate(date) {
         return moment(date).format("DD-MMM-YYYY")
      },

      truncateLongText(text, limit) {
         return truncateLongText(text, limit)
      },

      onSubmit() {

      },

      viewContract() {
         let ukey = this.$refs.contractDetailsFormRef.getUniqueKey()
         let access_key = this.$refs.contractDetailsFormRef.getInternalAccessKey()
         const routeData = this.$router.resolve({ name: 'clientContract', params: { unique_key: ukey, access_key: access_key } });
         window.open(routeData.href, '_blank');
      },

      ChangeOption(isOption) {
         this.isOption = isOption
         if (isOption === 'Activity' && this.$refs.contractActivityRef) {
            this.$refs.contractActivityRef.init(this.$route.params.id)
         }
      },

      getSectionContentById(section_id) {
         return this.$refs.editorRef.getSectionContentById(section_id)
      },

      replaceVariableFields(content) {
         if (isEmpty(content)) {
            return content
         }

         let prop_lead = this.$refs.contractDetailsFormRef ? this.$refs.contractDetailsFormRef.lead : null;
         if (!prop_lead) return content;

         // Resolve the BDM name for relations_manager from the logged-in user
         let bdmName = 'Martin Boyd'; // Default fallback
         const currentUser = this.$store.state.auth.currentUser;
         if (currentUser && (currentUser.first_name || currentUser.last_name)) {
            bdmName = ((currentUser.first_name || '') + ' ' + (currentUser.last_name || '')).trim();
         }

         let replacedContent = content;

         this.variableFieldsMapping.forEach(vfm => {
            // Backend-only variables (contract_term, termination_date, etc.) are resolved
            // by the backend's Rebuild Schedule — leave them untouched on the frontend.
            if (vfm.backend_only) return;

            // Extract the simple variable key from the replace_variable property
            let variableId = vfm.replace_variable;
            if (variableId === 'phone_number') variableId = 'contact_number';
            if (variableId === 'email') variableId = 'billing_email';
            
            // Resolve the value: relations_manager comes from BDM, others from lead
            let val;
            if (vfm.replace_variable === 'relations_manager') {
               val = bdmName;
            } else if (vfm.replace_variable === 'abn') {
               val = prop_lead.abn;
            } else {
               val = prop_lead[vfm.replace_variable];
            }

            let fillText = "<b>{No Record}</b>";
            if (!isEmpty(val)) {
                fillText = "<b>" + val + "</b>";
            } else if (vfm.replace_variable === 'abn') {
                fillText = "<b>N/A</b>";
            }

            // Escape variableId for use in regex
            let safeVarId = variableId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            
            // Primary regex: try to replace the entire <mark> block if it wraps just this variable cleanly
            let rgxMark = new RegExp(`<mark[^>]*>\\s*\\{\\{\\s*<span[^>]*>!\\*<\\/span>\\s*<span id="variable_value_${safeVarId}"[^>]*>.*?<\\/span>\\s*<span[^>]*>\\*!<\\/span>\\s*\\}\\}\\s*<\\/mark>`, 'gi');
            
            // Fallback regex: replaces just the inner {{ ... }} if nested inside other marks (like first/last name blocks)
            let rgxFallback = new RegExp(`\\{\\{[^{}]*id="variable_value_${safeVarId}"[^{}]*\\}\\}`, 'gi');

            if (rgxMark.test(replacedContent)) {
               replacedContent = replacedContent.replace(rgxMark, fillText);
            } else {
               replacedContent = replacedContent.replace(rgxFallback, fillText);
            }
         });

         return replacedContent
      },

      // ---- JSON Section Editing Helpers ----
      SECTION_ID_MAP() {
         return {
            MasterTerms:            'start_master_terms_section',
            ServiceModuleStandard:  'start_standard_section',
            ServiceModuleTerm:      'start_term_section',
            ServiceModuleSubscription: 'start_subscription_section',
            ScheduleTable:          'start_schedule_table_section',
            ExecutionPage:          'start_execution_page_section',
         };
      },

      formatSectionLabel(type) {
         const labels = {
            MasterTerms: 'Master Terms',
            ServiceModuleStandard: 'Service Module — Standard',
            ServiceModuleTerm: 'Service Module — Term',
            ServiceModuleSubscription: 'Service Module — Subscription',
            ScheduleTable: 'Schedule',
            ExecutionPage: 'Execution Page',
         };
         return labels[type] || type;
      },

      // Build the scaffold HTML that the WysiwygEditor renders:
      // one non-editable wrapper div per section, with a nested editable div.
      buildSectionsScaffold(sections) {
         const idMap = this.SECTION_ID_MAP();
         const self = this;

         // Styled placeholder for automated/system-generated content
         const automatedPlaceholder = (title, description) =>
            `<div contenteditable="false" style="background-color: #f9f9f9; padding: 25px; text-align: center; border: 1px solid #ddd; border-radius: 4px; margin: 10px 0;">` +
            `<h3 style="color: #435666; margin: 0; font-family: sans-serif; font-size: 15px;">[${title}]</h3>` +
            `<p style="font-size: 12px; color: #888; font-family: sans-serif; margin-top: 5px;">${description}</p>` +
            `</div>`;

         return sections.map(function(section) {
            if (!section || !section.type) return '';
            if (section.is_hidden) return '';
            const sectionType = section.type;
            let sectionBody = section.content || '';
            sectionBody = sectionBody.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:');
            const sectionId = idMap[sectionType] || ('start_' + sectionType.toLowerCase() + '_section');
            const label = self.formatSectionLabel(sectionType);

            // Replace automated system markers with styled placeholders

            // 2. Xodo signature placements
            // The stored content wraps markers in various HTML tags like:
            //   <mark ...><b>{{XODO_INTERNAL_SIGNATURE}}</b></mark>
            // Use a broad regex to catch any tag wrapping around the markers
            sectionBody = sectionBody.replace(
               /(<[^>]*>\s*)*\{\{XODO_INTERNAL_SIGNATURE\}\}(\s*<\/[^>]*>)*/gi,
               automatedPlaceholder('Automated Internal Signature', 'Vertex internal signature will be placed here when the contract is sent.')
            );
            sectionBody = sectionBody.replace(
               /(<[^>]*>\s*)*\{\{XODO_EXTERNAL_SIGNATURE\}\}(\s*<\/[^>]*>)*/gi,
               automatedPlaceholder('Automated External Signature', 'Client signature will be placed here when the contract is sent.')
            );

            return '<section id="' + sectionType + '_section" class="non-editable-section" contenteditable="false" style="margin-bottom: 30px;">' +
               '<h4 style="color: #00a0c1; text-align: center; margin-bottom: 15px; font-family: Roboto, sans-serif;" contenteditable="false">' + label + '</h4>' +
               '<div id="' + sectionId + '" class="editable-content-inside" contenteditable="true" style="border: 1px dashed #ccc; padding: 20px; border-radius: 4px; min-height: 50px; background-color: #fff;">' + sectionBody + '</div>' +
               '</section>';
         }).join('');
      },


      // After saving, read each section's content back from editor by section ID.
      // Hidden sections are not in TinyMCE, so preserve their stored content.
      readSectionsFromEditor() {
         const idMap = this.SECTION_ID_MAP();
         return this.contractSections.map((section, idx) => {
            let editorContent = section.content || '';
            if (!section.is_hidden) {
               const sectionId = idMap[section.type] || ('start_' + section.type.toLowerCase() + '_section');
               editorContent = this.getSectionContentById(sectionId) || section.content || '';
               editorContent = editorContent.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:');

               // Restore automated placeholders back to original markers before saving
               editorContent = editorContent.replace(
                  /<div[^>]*contenteditable="false"[^>]*>[\s\S]*?\[Automated Internal Signature\][\s\S]*?<\/div>/gi,
                  '<b>{{XODO_INTERNAL_SIGNATURE}}</b>'
               );
               editorContent = editorContent.replace(
                  /<div[^>]*contenteditable="false"[^>]*>[\s\S]*?\[Automated External Signature\][\s\S]*?<\/div>/gi,
                  '<b>{{XODO_EXTERNAL_SIGNATURE}}</b>'
               );
            }
            return { ...section, content: editorContent, sequence: idx + 1 };
         });
      },


      moveSectionUp(idx) {
         if (idx <= 0) return;
         const arr = [...this.contractSections];
         [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
         arr.forEach((s, i) => { s.sequence = i + 1; });
         this.contractSections = arr;
         this.$nextTick(() => this.reinjectSectionsIntoEditor());
      },

      moveSectionDown(idx) {
         if (idx >= this.contractSections.length - 1) return;
         const arr = [...this.contractSections];
         [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
         arr.forEach((s, i) => { s.sequence = i + 1; });
         this.contractSections = arr;
         this.$nextTick(() => this.reinjectSectionsIntoEditor());
      },

      toggleSectionHidden(idx) {
         if (this.contractSections[idx]) {
            this.contractSections[idx].is_hidden = !this.contractSections[idx].is_hidden;
         }
      },

      // Re-inject all sections into editor (used after reorder)
      reinjectSectionsIntoEditor() {
         if (!this.$refs.editorRef || this.contractSections.length === 0) return;
         const scaffold = this.buildSectionsScaffold(this.contractSections);
         this.$refs.editorRef.setContent(scaffold, this.isContractLocked);
      },

      // When ContractSequence emits 'save', update editor with new order AND persist to backend
      async onSectionSequenceSave() {
         // 1. Re-inject scaffold into TinyMCE in new order (hidden sections excluded)
         if (this.$refs.editorRef && this.contractSections.length > 0) {
            const scaffold = this.buildSectionsScaffold(this.contractSections);
            this.$refs.editorRef.setContent(scaffold, this.isContractLocked);
         }
         // 2. Persist the updated sequence + visibility to the backend
         try {
            const sectionsJson = JSON.stringify(this.contractSections);
            await this.$refs.contractDetailsFormRef.updateContract({
               contract_sections_json_draft: sectionsJson,
            });
         } catch (e) {
            this.$toast.error('Failed to save sequence to server.');
         }
      },

      // Insert block content from right panel into whichever section last clicked in editor
      insertBlockIntoActiveSection(htmlContent) {
         // For WysiwygEditor approach: just append to editor at cursor position
         if (this.$refs.editorRef) {
            this.$refs.editorRef.addContent(htmlContent);
         }
      },

      replaceActiveSection(htmlContent) {
         if (this.$refs.editorRef) {
            this.$refs.editorRef.addContent(htmlContent);
         }
      },

      // ---- Revision Panel Methods ----
      locateRevision(rev) {
         if (rev.content && rev.content.startsWith(">>>") && (rev.content.includes(':\n"') || rev.content.includes(':\r\n"'))) {
            if (this.$refs.editorRef && this.$refs.editorRef.findAndHighlight) {
               const success = this.$refs.editorRef.findAndHighlight(rev.content);
               if (success) {
                  return;
               }
            }
         }

         // Fallback to scrolling to the actual HTML block natively.
         if (rev.section_name) {
            const idMap = this.SECTION_ID_MAP();
            const sectionTarget = idMap[rev.section_name] || ('start_' + rev.section_name.toLowerCase() + '_section');
            if (this.$refs.editorRef && this.$refs.editorRef.scrollToElement) {
               this.$refs.editorRef.scrollToElement(sectionTarget);
            } else {
               this.$toast && this.$toast.info("Could not extract exact text string to locate.");
            }
         }
      },

      async loadAndShowRevisions(skipTabChange = false) {
         if (skipTabChange !== true) {
            this.ChangeOption('Revisions');
         }
         if (this.sectionRevisions.length > 0 && !this.isLoadingRevisions) return; // already loaded
         this.isLoadingRevisions = true;
         try {
            const res = await this.$axios.get(`/contracts/${this.contract_id}/revisions`);
            this.sectionRevisions = res.data.revisions || [];
            this.revisionUnreadCounts = res.data.unread_counts || {};
            this.hasLoadedRevisions = true;
         } catch (e) {
            console.warn('[loadAndShowRevisions]', e);
         } finally {
            this.isLoadingRevisions = false;
         }
      },

      async resolveRevision(rev) {
         this.resolvingRevisionId = rev.id;
         try {
            await this.$axios.patch(`/contracts/${this.contract_id}/revisions/${rev.id}/resolve`);
            rev.is_resolved = true;
            this.hasRevisionNotSentState = true;
            this.$toast && this.$toast.success('Revision marked as resolved.');

            if (this.unresolvedRevisions.length === 0 && (this.contractStatus === 1 || this.contractStatus === 2 || this.contractStatus === 3 || this.contractStatus === 8)) {
               this.pendingAction = 'resendContract';
               this.confirmModalTitle = 'Resend Contract';
               this.confirmModalMessage = 'All client revisions have been resolved. Would you like to resend the contract now to reflect these changes in the PDF and Xodo Sign?';
               this.showConfirmModal = true;
            }
         } catch (e) {
            console.warn('[resolveRevision]', e);
            this.$toast && this.$toast.error('Could not resolve revision.');
         } finally {
            this.resolvingRevisionId = null;
         }
      },

      async toggleChat(revId) {
         if (this.activeChatRevisionId === revId) {
            this.activeChatRevisionId = null;
            this.chatComments = [];
            return;
         }
         this.activeChatRevisionId = revId;
         this.chatComments = [];

         // Mark the other party's comments as read (BDM opening → mark client comments as read)
         try { await this.$axios.patch(`/contracts/${this.contract_id}/revisions/${revId}/comments/read`); } catch(e) { /* silent */ }
         delete this.revisionUnreadCounts[revId];

         try {
           const res = await this.$axios.get(`/contracts/${this.contract_id}/revisions/${revId}/comments`);
           if(res.data && res.data.comments) {
              this.chatComments = res.data.comments;
           }
         } catch (e) {
            this.$toast && this.$toast.error('Failed to load chat thread.');
         }
      },

      async postComment(revId) {
         console.log('[postComment] called with revId:', revId, 'contract_id:', this.contract_id, 'text:', this.newCommentText);
         if(!this.newCommentText.trim()) return;
         this.isSubmittingComment = true;
         try {
           console.log('[postComment] sending POST to:', `/contracts/${this.contract_id}/revisions/${revId}/comments`);
           const res = await this.$axios.post(`/contracts/${this.contract_id}/revisions/${revId}/comments`, {
              comment_text: this.newCommentText
           });
           console.log('[postComment] response:', JSON.stringify(res.data));
           if(res.data && res.data.comment) {
             this.chatComments.push(res.data.comment);
             this.newCommentText = '';
             this.$toast && this.$toast.success('Reply sent.');
           } else {
             console.warn('[postComment] no comment in response:', res.data);
           }
         } catch (e) {
           console.error('[postComment] Error:', e.response ? e.response.status : e.message, e.response ? JSON.stringify(e.response.data) : '');
           this.$toast && this.$toast.error('Failed to post comment.');
         } finally {
           console.log('[postComment] finally block reached');
           this.isSubmittingComment = false;
         }
      },

      formatRevisionDate(dateStr) {
         if (!dateStr) return '';
         const d = new Date(dateStr);
         return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      },

      // Add these inside your methods: {} object


      getStatusColor(status_code) {
         if (status_code === 3 && this.hasLoadedRevisions && this.unresolvedRevisions.length === 0) {
            return this.colorForStatus.sent;
         }
         switch (status_code) {
            case 0: return this.colorForStatus.draft;
            case 1: return this.colorForStatus.sent;
            case 2: return this.colorForStatus.viewed;
            case 3: return this.colorForStatus.in_review;
            case 4: return this.colorForStatus.executed;
            case 5: return this.colorForStatus.cancelled;
            case 6: return this.colorForStatus.pending_approval;
            case 7: return this.colorForStatus.approved;
            case 8: return this.colorForStatus.declined;
            case 9: return this.colorForStatus.expired || '#EB1C24';
            default: return this.colorForStatus.draft;
         }
      },

      getStatusText(status_code) {
         if (status_code === 3 && this.hasLoadedRevisions && this.unresolvedRevisions.length === 0) {
            return "Sent";
         }
         switch (status_code) {
            case 1:
               return "Sent";
            case 2:
               return "Viewed";
            case 3:
               return "In Review";
            case 4:
               return "Executed";
            case 5:
               return "Cancelled";
            case 6:
               return "Pending Approval";
            case 7:
               return "Approved";
            case 8:
               return "Declined";
            case 9:
               return "Expired";
            default: // Handles 0 and any other case
               return "Draft";
         }
      },

      getContractStatusBadgeClass(statusCode) {
         if (statusCode === 3 && this.hasLoadedRevisions && this.unresolvedRevisions.length === 0) {
            return 'spark-pill-badge--sent';
         }
         switch (Number(statusCode)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--viewed';
            case 3: return 'spark-pill-badge--in-review';
            case 4: return 'spark-pill-badge--executed';
            case 5: return 'spark-pill-badge--cancelled';
            case 6: return 'spark-pill-badge--pending-approval';
            case 7: return 'spark-pill-badge--approved';
            case 8: return 'spark-pill-badge--declined';
            case 9: return 'spark-pill-badge--expired';
            default: return 'spark-pill-badge--draft';
         }
      },

      getContractStatusIcon(statusCode) {
         if (statusCode === 3 && this.hasLoadedRevisions && this.unresolvedRevisions.length === 0) {
            return 'fa-solid fa-paper-plane';
         }
         switch (Number(statusCode)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-eye';
            case 3: return 'fa-solid fa-hourglass-half';
            case 4: return 'fa-solid fa-file-signature';
            case 5: return 'fa-solid fa-ban';
            case 6: return 'fa-solid fa-clock-rotate-left';
            case 7: return 'fa-solid fa-check';
            case 8: return 'fa-solid fa-xmark';
            case 9: return 'fa-solid fa-calendar-xmark';
            default: return 'fa-solid fa-pen';
         }
      },
   }
})

</script>
<style scoped>
.modal-overlay {
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background-color: rgba(0, 0, 0, 0.5);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 9999;
}

.modal-overlay .modal-content {
   background: white;
   position: relative;
   box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.nav-tabs .nav-link.active {
    background-color: #00A0C1;
    color: #fff !important;
    border-color: #00A0C1;
}

.nav-tabs .nav-link {
    color: black;
}
.audit-menu-tab {
   border: none;
   /* margin: 1rem; */
}

.audit-menu-tab .nav-link {
   color: #435666;
   height: 40px;
   width: 200px;
   min-width: 95px;
   /* padding: 1rem; */
   font-weight: 400;
   border: 1px solid #A3C0D1;
}

button.nav-link {
   border-radius: 0px !important;
}

.audit-menu-tab .nav-link.active {
   background-color: #00A0C1;
   color: #fff;
   font-weight: 900;
}

.audit-header-date {
   justify-content: start;
}

@media (max-width: 767px) {

   .audit-menu-tab .nav-link {
      width: auto;
      height: auto;
   }

   .activity-log-button,
   .activity-log {
      display: none;
   }

}

@media (max-width: 575px) {
   .audit-header {
      flex-direction: column-reverse;
   }

   .audit-header-date {
      justify-content: center;
   }

}

.editor {
   display: flex;
}

.toolbar {
   /* Toolbar styles */
   display: flex;
   flex-direction: column;
}

.content {
   /* Content area styles */
   margin-left: 20px;
}

.cover-page {
   /* Cover page styles */
   width: 500px;
   height: 300px;
   border: 1px solid #ccc;
   position: relative;
}

.cover-image {
   /* Cover image styles */
   width: 100%;
   height: 100%;
   object-fit: cover;
}

.logo {
   /* Logo styles */
   position: absolute;
   top: 10px;
   left: 10px;
}

.cover-text {
   /* Cover text styles */
   position: absolute;
   bottom: 10px;
   left: 10px;
   color: white;
}

.details {
   /* Details section styles */
   margin-top: 20px;
}

.detail-item {
   /* Detail item styles */
   margin-bottom: 10px;
}

.block-button-container {
   display: flex;
   align-items: center;
   /* Vertically align icons */
   gap: 15px;
   /* Adjust spacing between buttons */
}

@media (min-width: 1900px) {

   /* Adjust breakpoint as needed */
   .block-button-container {
      gap: 6px;
      /* Larger gap for larger screens */
   }
}

.collapsible-content {
   padding: 10px;
   margin-top: 10px;
   overflow: hidden;
   transition: width 0.3s ease;
   /* Float the element to the right */
}

.collapse-transition {
   transition: width 0.3s ease;
}



.hidden {
   display: none;
}

/* Scroll bar */
/* width */
::-webkit-scrollbar {
   width: 10px;
}

/* Track */
::-webkit-scrollbar-track {
   background: #f1f1f1;
}

/* Handle */
::-webkit-scrollbar-thumb {
   background: #00a0c1;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
   background: #00a0c1;
}

.version-status-icon {
   display: inline-flex;
   justify-content: center;
   align-items: center;
   min-width: 45px;
   height: 24px;
   padding: 0 10px;
   /* A bit more padding for status text */
   border-radius: 3px;
   color: white;
   font-size: 14px;
   /* Slightly larger font for the title area */
   font-weight: bold;
   text-transform: none;
   white-space: nowrap;
   vertical-align: middle;
   /* Align nicely with the title text */
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* --- PDF SPECIFIC STYLES (REVISED) --- */
#pdf-content-area {
   position: absolute;
   left: -9999px;
   top: 0;
   width: 210mm;
   padding: 20mm;
   box-sizing: border-box;
   background-color: white;
   font-family: 'Helvetica', 'Arial', sans-serif;
   /* Use PDF-safe fonts */
   font-size: 11pt;
   line-height: 1.5;
   color: #333333;
}

/* This is our new container for each major section */
.pdf-page-section {
   page-break-inside: avoid !important;
   /* TRY NOT to break this section */
   page-break-after: auto !important;
   margin-bottom: 15px;
   /* Add some space between sections */
}

/* Tame large images inside the PDF content */
#pdf-content-area img {
   max-width: 100% !important;
   height: auto !important;
   page-break-inside: avoid !important;
}

#pdf-content-area p,
#pdf-content-area li,
#pdf-content-area .pdf-product-table tr {
   page-break-inside: avoid !important;
}

#pdf-content-area h1,
#pdf-content-area h2,
#pdf-content-area h3,
#pdf-content-area h4 {
   color: #00a0c1;
   line-height: 1.2;
   page-break-inside: avoid !important;
   /* This rule prevents a heading from being the last line on a page */
   page-break-after: avoid !important;
}

#pdf-content-area h2.pdf-title {
   font-size: 24pt;
   text-align: center;
   margin-bottom: 1.5em;
}

#pdf-content-area h4.pdf-section-title {
   font-size: 14pt;
   border-bottom: 2px solid #00a0c1;
   padding-bottom: 5px;
   margin-bottom: 1em;
}

.pdf-product-table {
   width: 100%;
   border-collapse: collapse;
   margin: 20px 0;
   border: 1px solid #cccccc;
   page-break-inside: auto;
   /* A table itself is allowed to break */
}

.pdf-product-table th,
.pdf-product-table td {
   padding: 8px 12px;
   border: 1px solid #e0e0e0;
   text-align: left;
   font-size: 10pt;
}

.pdf-product-table th {
   background-color: #00a0c1;
   color: white;
   font-weight: bold;
}

.pdf-product-table tfoot .pdf-total-row {
   font-weight: bold;
   background-color: #f5f5f5;
}

/* ADD THIS TO FIX THE ISSUE OF PRICING TABLE PUSHING CONTENT DOWN ON SMALLER SCREENS */
@media (max-width: 1499px) {
   .collapsible-content {
      display: block;
      /* Use block display to prevent flexbox from wrapping the column down */
   }

   .contract-variable-fields,
   .contract-details-form,
   .contract-activity {
      width: 100%;
      /* Ensure each form/component takes full width */
      margin-bottom: 15px;
      /* Add some spacing between sections */
   }
}

/*
  TARGET: The <button> element, which acts as our circular container.
*/
.btn-svg-icon {
   width: 30px;
   /* 👈 Increased from 34px */
   height: 30px;
   /* 👈 Increased from 34px */
   border-radius: 50%;
   /* This makes the button a circle */

   /* These three lines perfectly center the icon inside the circle */
   display: flex;
   align-items: center;
   justify-content: center;

   /* A few resets and a smooth transition for the hover effect */
   background-color: transparent;
   border: none;
   padding: 0;
   cursor: pointer;
   transition: background-color 0.2s ease;
}

/*
  TARGET: The <img> icon itself.
  PURPOSE: Make the icon smaller than the button to create a padding effect.
*/
.icon-svg {
   width: 30px;
   /* 👈 Increased from 20px */
   height: 30px;
   /* 👈 Increased from 20px */
}


/* --- THE HOVER EFFECT --- */

/*
  Step 1: Change the BUTTON's background on hover.
  Because the button has border-radius: 50%, the background will be a circle.
*/
.btn-svg-icon:hover {
   background-color: #00A0C1;
   /* The solid teal color */
}

/*
  Step 2: Change the ICON's color when the parent button is hovered.
  The filter trick makes your teal SVG icon appear white.
*/
.btn-svg-icon:hover .icon-svg {
   filter: brightness(0) invert(1);
}

/* Make inline SVG icons turn white on hover */
.btn-svg-icon:hover svg circle {
   stroke: white;
}
.btn-svg-icon:hover svg path {
   fill: white;
   stroke: white;
}
.btn-svg-icon:hover svg text {
   fill: white;
}

/*
  TARGET: The button when it is disabled.
  PURPOSE: Change the button's appearance to a gray color and change the cursor.
*/
.btn-svg-icon:disabled {
   background-color: #f0f0f0;
   /* A light gray background */
   cursor: not-allowed;
   transition: none;
   /* Disable the transition to prevent the hover effect from flashing */
}

/*
  TARGET: The icon inside the disabled button.
  PURPOSE: Change the icon's color to a darker gray.
*/
.btn-svg-icon:disabled .icon-svg {
   filter: grayscale(100%) brightness(50%);
   /* Make the icon gray */
}

/*
  TARGET: Override the hover effect for the disabled button.
  PURPOSE: Ensure no color change occurs when hovering over the disabled button.
*/
.btn-svg-icon:disabled:hover {
   background-color: #f0f0f0;
   /* Keeps the same gray background */
   cursor: not-allowed;
}

/*
  TARGET: The icon inside the disabled button on hover.
  PURPOSE: Ensure the icon's color does not change on hover.
*/
.btn-svg-icon:disabled:hover .icon-svg {
   filter: grayscale(100%) brightness(50%);
   /* Keeps the same gray icon color */
}

/* ===== BDM Revision Panel ===== */
.revision-count-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background: #e65100;
   color: white;
   font-size: 10px;
   font-weight: 700;
   border-radius: 50%;
   min-width: 18px;
   height: 18px;
   padding: 0 4px;
   margin-left: 6px;
   vertical-align: middle;
}

.revision-panel {
   padding: 8px 4px;
   max-height: 600px;
   overflow-y: auto;
}

.revision-card {
   background: #fff;
   border-radius: 8px;
   border: 1px solid #eee;
   border-left: 4px solid #9e9e9e;
   padding: 12px 14px;
   margin-bottom: 10px;
   transition: box-shadow 0.15s;
}
.revision-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.revision-card.revision-comment  { border-left-color: #1565c0; }
.revision-card.revision-revision { border-left-color: #2e7d32; }
.revision-card.revision-delete  { border-left-color: #b71c1c; }

.revision-card-header {
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 4px;
   flex-wrap: wrap;
}

.revision-action-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   font-size: 11px;
   font-weight: 700;
   padding: 2px 10px;
   border-radius: 12px;
   background: #f5f5f5;
   color: #333;
}

.revision-section-tag {
   font-size: 11px;
   background: #e3f2fd;
   color: #0d47a1;
   padding: 2px 8px;
   border-radius: 10px;
   font-weight: 600;
}

.revision-submitter {
   font-size: 11px;
   color: #888;
   margin-bottom: 6px;
}

.revision-body {
   font-size: 13px;
   color: #333;
   background: #fafafa;
   border: 1px solid #eee;
   border-radius: 6px;
   padding: 8px 10px;
   margin-bottom: 8px;
   white-space: pre-wrap;
   line-height: 1.5;
}

.revision-card-footer {
   display: flex;
   justify-content: flex-end;
}

.btn-resolve {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 5px 14px;
   border-radius: 16px;
   border: none;
   background: #e8f5e9;
   color: #2e7d32;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   transition: background 0.15s;
}
.btn-resolve:hover:not(:disabled) { background: #c8e6c9; }
.btn-resolve:disabled { opacity: 0.5; cursor: not-allowed; }

/* ===== BDM JSON Section Editing ===== */
.contract-section-editable {
   position: relative;
   padding-bottom: 36px;
   border: 1px solid transparent;
   border-radius: 6px;
   transition: border-color 0.15s;
}
.contract-section-editable:hover {
   border-color: #b3e5fc;
}
.contract-section-editable.section-active {
   border-color: #00a0c1;
   box-shadow: 0 0 0 2px rgba(0,160,193,0.15);
}

.section-type-label {
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.6px;
   color: #00a0c1;
   margin-bottom: 4px;
   padding: 0 2px;
}

.section-editable-body {
   min-height: 40px;
   outline: none;
   border-radius: 4px;
   padding: 4px;
   transition: background 0.1s;
}
.section-editable-body:focus {
   background: #f0faff;
}

.section-actions {
   position: absolute;
   bottom: 6px;
   right: 8px;
   display: flex;
   gap: 4px;
   opacity: 0;
   transition: opacity 0.15s;
}
.contract-section-editable:hover .section-actions {
   opacity: 1;
}

.section-action-btn {
   background: #e3f2fd;
   border: none;
   border-radius: 4px;
   padding: 3px 8px;
   font-size: 11px;
   color: #0d47a1;
   cursor: pointer;
   transition: background 0.15s;
}
.section-action-btn:hover:not(:disabled) { background: #bbdefb; }
.section-action-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.section-action-btn.danger { background: #fce4ec; color: #b71c1c; }
.section-action-btn.danger:hover { background: #f8bbd0; }

/* ===== Sections Reorder Panel ===== */
.sections-panel { padding: 8px 4px; }
.sections-panel-hint {
   font-size: 11px; color: #888; margin-bottom: 8px;
}
.section-row {
   display: flex; justify-content: space-between; align-items: center;
   padding: 7px 10px; margin-bottom: 4px;
   background: #f8fafc; border: 1px solid #e0e0e0; border-radius: 6px;
   transition: background 0.15s;
}
.section-row:hover { background: #eaf6fb; }
.section-row-hidden { opacity: 0.45; }
.section-row-label {
   font-size: 12px; font-weight: 600; color: #333;
   display: flex; align-items: center; gap: 6px;
}
.section-drag-icon { color: #bbb; cursor: grab; }
.section-row-actions { display: flex; gap: 4px; }
.btn-section-move, .btn-section-eye {
   background: none; border: none; padding: 2px 6px;
   font-size: 12px; color: #555; cursor: pointer; border-radius: 4px;
   transition: background 0.12s;
}
.btn-section-move:hover:not(:disabled) { background: #dbeafe; color: #1d4ed8; }
.btn-section-eye:hover { background: #fef9c3; color: #854d0e; }
.btn-section-move:disabled { opacity: 0.3; cursor: not-allowed; }

</style>
